import { ComponentProps } from 'react'
import { Link } from 'react-router-dom'

import { Avatar, Box, EnhancedDimension, Text } from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'

import {
  EmployeeQuickView,
  EmployeeQuickViewProps,
} from 'modules/employees/components/EmployeeQuickView'

const variants = ['small', 'medium', 'large'] as const
type Variant = (typeof variants)[number]

type Props = {
  employee: {
    preferredName?: string | null
    id: string
    access: {
      avatar?: string | null
      fullName: string
    }
  }
  hideAvatar?: boolean
  linkProfile?: boolean
  variant?: Variant
  disableQuickView?: boolean
  textContainerWidth?: EnhancedDimension
} & EmployeeQuickViewProps

export const InlineEmployee: React.FC<Props> = (props) => {
  const {
    employee,
    hideAvatar = false,
    linkProfile,
    variant = 'medium',
    disableQuickView = false,
    textContainerWidth = 'full',
    ...quickViewProps
  } = props
  const { navigation } = useNavigation()

  const avatarSizes: Record<Variant, EnhancedDimension> = {
    small: 's24',
    medium: 's32',
    large: 's48',
  }

  const textSizes: Record<Variant, ComponentProps<typeof Text.Normal>['size']> = {
    small: '200',
    medium: '300',
    large: '300',
  }

  const gap: Record<Variant, ComponentProps<typeof Box>['gap']> = {
    small: 's6',
    medium: 's8',
    large: 's12',
  }

  const preferredName = employee.preferredName || employee.access.fullName
  const displayedName = quickViewProps.usePreferredName ? preferredName : employee.access.fullName
  const inlineEmployee = (
    <Box gap={gap[variant]} flexDirection="row" alignItems="center">
      {!hideAvatar && (
        <Avatar
          label={displayedName}
          url={buildUrl(employee.access?.avatar)}
          size={avatarSizes[variant]}
        />
      )}
      <Box maxWidth={textContainerWidth}>
        <Text.Normal
          size={textSizes[variant]}
          whiteSpace="nowrap"
          ellipsis
          title={null}
          className="notranslate"
        >
          {displayedName}
        </Text.Normal>
      </Box>
    </Box>
  )

  return (
    <EmployeeQuickView enabled={!disableQuickView} employee={employee} {...quickViewProps}>
      <Box inline>
        {linkProfile ? (
          <Link
            onClick={quickViewProps.onViewProfile}
            to={navigation.employees.id.fullPath({ id: employee.id })}
            style={{ display: 'block' }}
          >
            {inlineEmployee}
          </Link>
        ) : (
          <>{inlineEmployee}</>
        )}
      </Box>
    </EmployeeQuickView>
  )
}
